/**
 * #3991 (F9): when a feature is about to REFUSE the user because it believes we are offline, what
 * should it do first — refuse, allow, or re-probe?
 *
 * This is the highest-stakes decision in the offline work, which is why it is a pure function with
 * a table test rather than an if-chain buried in a service. It can be wrong in two directions and
 * both are bad:
 *
 *   - Too eager to trust the cached Offline state -> a user who is genuinely online is refused, and
 *     stays refused for minutes at a time. The connection state is refreshed by an online/offline
 *     event, a focus/visibility change, a failed request elsewhere, and — since df-eabc7656368c —
 *     the six-minute foreground tick, which re-checks while the app believes it is offline. Six
 *     minutes is a long time to keep telling somebody they cannot do something. One unlucky probe
 *     (a server hiccup, a captive portal, a cold-boot ping that timed out) can leave it latched on
 *     Offline on a device that works perfectly well. Before this signal existed these paths read
 *     navigator.onLine, got `true`, and simply worked — so refusing here is a regression.
 *
 *   - Too eager to re-probe -> every refusal on a genuinely offline device costs a probe timeout of
 *     dead air (REACHABILITY_PROBE_TIMEOUT_MS, 10s) before the user is told anything at all. That is
 *     the COMMON case, and the answer was already known.
 *
 * So the rules, in order, are about what we can already trust:
 */
export enum RefusalRecheckDecision {
  /** We believe we are online. Let the user through; no probe needed. */
  Allow = 'allow',
  /** We are confident we are offline — either the browser says so outright, or our own probe said so
   *  moments ago. Refuse immediately: a probe would only add silence to an answer we already have. */
  Refuse = 'refuse',
  /** The browser thinks there is a network, our state says offline, and that verdict is old enough
   *  to be stale. Probe before refusing — and if it succeeds, heal the latched state. */
  Recheck = 'recheck',
}

/** How fresh must a probe verdict be for us to trust it without re-probing? (milliseconds)
 *
 *  30s is chosen to be comfortably longer than a user's tap-to-tap reaction time — so a genuinely
 *  offline user tapping around gets instant, honest refusals rather than a 10s hang each time — and
 *  comfortably shorter than the stretch over which a stale latch becomes plausible.
 *
 *  The six-minute tick added by df-eabc7656368c barely touches this. It leaves a verdict "fresh"
 *  for 30 seconds out of every 360, so about one refusal in twelve trusts the tick's answer instead
 *  of checking again — and in those moments the answer came from a real check seconds earlier, so
 *  trusting it is the right call. The other eleven behave exactly as they always did. */
export const RECENT_PROBE_TRUST_MS = 30_000;

export function decideRefusalRecheck(input: {
  /** Our own belief: `navigator.onLine !== false && state === Online`. */
  isLikelyOnline: boolean;
  /** Did the browser flatly say there is no network? This is the ONE direction navigator.onLine can
   *  be trusted — it is `true` that means "unknown", not `false`. */
  rawFlagSaysDefinitelyOffline: boolean;
  /** How long ago did our own probe last settle (either way)? `Infinity` when it never has — which
   *  is the case at boot, and correctly forces a real probe rather than trusting a verdict that
   *  does not exist. */
  msSinceLastProbe: number;
}): RefusalRecheckDecision {
  if (input.isLikelyOnline) return RefusalRecheckDecision.Allow;
  if (input.rawFlagSaysDefinitelyOffline) return RefusalRecheckDecision.Refuse;
  if (input.msSinceLastProbe < RECENT_PROBE_TRUST_MS) return RefusalRecheckDecision.Refuse;
  return RefusalRecheckDecision.Recheck;
}
